import React, { useState } from 'react';
import { Button, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import type { HistorialClinico, ProcedimientoVista } from '@api/Consents/Consent';
import { ConsentApi } from '@api/Consents/ConsentApi';
import { avisarTerminado } from '@src/shared/utils/appBridge';
import { conPunto, fechaCorta, fechaHora } from '@src/shared/utils/consent';
import { detectarPlataforma } from '@src/shared/utils/stores';
import { AvisoSnackbar, Cabecera, Cargando, ErrorCarga, Listo, Seccion } from './common';
import { FirmaConsentimiento } from './Consentimiento';
import { FormularioHistorial, ResumenHistorial } from './HistorialClinico';
import { useExpediente } from './useExpediente';

/**
 * El contrato de Studio DENTRO de la app de la clienta (`?app=1`). Lo ÚNICO obligatorio es firmar
 * el contrato (Irving, 10/oct/2026); ya firmado se le ofrece llenar su historial clínico como
 * opcional, ve sus procedimientos y, cuando uno ya se realizó, puede dejar su observación. El
 * procedimiento del día lo firma en la sucursal, con el QR del Punto de Venta.
 */
const ContratoApp: React.FC<{ id: string }> = ({ id }) => {
    const modo = { modo: 'app' as const };
    const { vista, cargando, errorCarga, ocupado, aviso, setAviso, ejecutar, recargar } = useExpediente(id, modo);
    const [recienFirmado, setRecienFirmado] = useState(false);
    const [llenandoHistorial, setLlenandoHistorial] = useState(false);
    const plataforma = detectarPlataforma();

    if (cargando) return <Cargando />;
    if (errorCarga || !vista) return <ErrorCarga mensaje={errorCarga?.mensaje} estado={errorCarga?.estado} />;

    const anulado = vista.status === 'void';
    const firmado = vista.consentimiento.firmado;
    const primerNombre = vista.clienta.nombre.split(' ')[0];

    const firmar = async (firma: string) => {
        const nueva = await ejecutar('firma', () => ConsentApi.firmarConsentimiento(modo, id, firma));
        if (nueva) {
            setRecienFirmado(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const guardarHistorial = async (historial: HistorialClinico) => {
        const ok = await ejecutar(
            'historial',
            async () => {
                await ConsentApi.guardarHistorial(modo, id, historial);
                return true;
            },
            'Gracias, tu historial quedó guardado.'
        );
        if (ok) {
            setLlenandoHistorial(false);
            await recargar();
        }
    };

    /** Dentro de la app de iPhone el PDF se abre en la misma vista; Android no muestra PDFs ahí. */
    const verPdf = async () => {
        const blob = await ejecutar('pdf', () => ConsentApi.pdf(modo, id));
        if (blob) window.location.href = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    };

    return (
        <Stack spacing={2.25} sx={{ maxWidth: 640, mx: 'auto', pb: 4 }}>
            <Cabecera
                folio={vista.folio}
                titulo={firmado ? 'Tu contrato' : 'Firma tu contrato'}
                subtitulo={
                    firmado
                        ? `${vista.clienta.nombre} · ${vista.sucursal.nombre}`
                        : `${primerNombre}, antes de tu cita de Studio necesitamos tu firma. Léelo con calma: toma un par de minutos.`
                }
            />

            {anulado && (
                <Seccion
                    titulo="Este contrato fue anulado"
                    detalle={vista.anulado?.motivo ? conPunto(vista.anulado.motivo) : 'Pregunta en tu sucursal.'}
                />
            )}

            {/* 1. Lo obligatorio: el contrato. */}
            {!anulado && !firmado && (
                <Seccion titulo="Tu consentimiento" detalle="Al final, firma en el recuadro con tu dedo.">
                    <FirmaConsentimiento vista={vista} firmando={ocupado === 'firma'} onFirmar={firmar} />
                </Seccion>
            )}

            {!anulado && firmado && (
                <Listo
                    titulo={recienFirmado ? '¡Listo! Tu contrato quedó firmado' : 'Tu contrato está firmado'}
                    detalle={
                        <>
                            {vista.consentimiento.firmadoEl
                                ? `${conPunto(`Firmado el ${fechaHora(vista.consentimiento.firmadoEl)}`)} `
                                : ''}
                            El día de tu cita, antes de empezar, firmas tu procedimiento en la sucursal.
                        </>
                    }
                >
                    <Button variant="contained" onClick={avisarTerminado} sx={{ mt: 0.5 }}>
                        Volver a la app
                    </Button>
                </Listo>
            )}

            {/* 2. Opcional, ya con el contrato firmado: el historial clínico. */}
            {!anulado && firmado && (
                <Seccion
                    titulo="Tu historial clínico"
                    detalle={
                        vista.historial
                            ? 'Gracias por compartirlo. Si algo cambió, corrígelo aquí.'
                            : 'Ayuda a tu artista a cuidar tu procedimiento. Si prefieres, lo llenan juntas en tu cita.'
                    }
                    accion={
                        vista.historial && !llenandoHistorial ? (
                            <Button
                                size="small"
                                startIcon={<EditOutlinedIcon fontSize="small" />}
                                onClick={() => setLlenandoHistorial(true)}
                            >
                                Editar
                            </Button>
                        ) : (
                            <Chip size="small" variant="outlined" label="Opcional" />
                        )
                    }
                >
                    {llenandoHistorial ? (
                        <FormularioHistorial
                            historial={vista.historial}
                            condiciones={vista.cuestionario.condiciones}
                            esClienta
                            guardando={ocupado === 'historial'}
                            onGuardar={guardarHistorial}
                            onCancelar={() => setLlenandoHistorial(false)}
                        />
                    ) : vista.historial ? (
                        <ResumenHistorial historial={vista.historial} condiciones={vista.cuestionario.condiciones} />
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={() => setLlenandoHistorial(true)}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Llenarlo ahora
                        </Button>
                    )}
                </Seccion>
            )}

            {/* Sus procedimientos: observaciones solo cuando ya se realizó. */}
            {firmado && vista.procedimientos.length > 0 && (
                <Seccion titulo="Tus procedimientos">
                    <Stack divider={<Divider flexItem />} spacing={1.75}>
                        {vista.procedimientos.map((p) => (
                            <ProcedimientoClienta
                                key={p.orderId}
                                p={p}
                                ocupado={ocupado === `nota-${p.orderId}`}
                                onObservar={async (texto) => {
                                    const nueva = await ejecutar(
                                        `nota-${p.orderId}`,
                                        () => ConsentApi.agregarObservacion(modo, id, p.orderId, texto),
                                        'Gracias, tu observación quedó guardada.'
                                    );
                                    return !!nueva;
                                }}
                            />
                        ))}
                    </Stack>
                </Seccion>
            )}

            {firmado && plataforma !== 'android' && (
                <Button
                    variant="text"
                    color="inherit"
                    startIcon={<PictureAsPdfOutlinedIcon />}
                    onClick={verPdf}
                    disabled={ocupado === 'pdf'}
                    sx={{ alignSelf: 'center' }}
                >
                    {ocupado === 'pdf' ? 'Preparando…' : 'Ver mi expediente en PDF'}
                </Button>
            )}

            <AvisoSnackbar aviso={aviso} onClose={() => setAviso(null)} />
        </Stack>
    );
};

/** Un procedimiento visto por la clienta: estado y, ya realizado, su observación. */
const ProcedimientoClienta: React.FC<{
    p: ProcedimientoVista;
    ocupado: boolean;
    onObservar: (texto: string) => Promise<boolean>;
}> = ({ p, ocupado, onObservar }) => {
    const [escribiendo, setEscribiendo] = useState(false);
    const [nota, setNota] = useState('');
    const estado = p.firmado
        ? { label: p.realizado ? 'Realizado' : 'Firmado', color: 'success' as const }
        : p.realizado
          ? { label: 'Realizado', color: 'success' as const }
          : { label: 'Se firma en tu cita', color: 'default' as const };

    const guardar = async () => {
        if (!nota.trim()) return;
        if (await onObservar(nota.trim())) {
            setNota('');
            setEscribiendo(false);
        }
    };

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600}>{p.servicio}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {p.fecha ? fechaHora(p.fecha) : 'En sucursal'} · {p.sucursal}
                    </Typography>
                </Stack>
                <Chip
                    size="small"
                    color={estado.color}
                    variant={estado.color === 'default' ? 'outlined' : 'filled'}
                    label={estado.label}
                />
            </Stack>

            {p.observaciones.length > 0 && (
                <Stack spacing={0.75} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                    {p.observaciones.map((o, i) => (
                        <Stack key={i} spacing={0.25}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {o.texto}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {o.parte === 'customer' ? 'Tú' : `Kabandú${o.por ? ` · ${o.por}` : ''}`}
                                {o.fecha ? ` · ${fechaCorta(o.fecha)}` : ''}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}

            {p.puedeObservar &&
                (escribiendo ? (
                    <Stack spacing={1}>
                        <TextField
                            label="¿Cómo te sentiste? ¿Algo que debamos saber?"
                            value={nota}
                            onChange={(e) => setNota(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            autoFocus
                            inputProps={{ maxLength: 1000 }}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button color="inherit" onClick={() => setEscribiendo(false)} disabled={ocupado}>
                                Cancelar
                            </Button>
                            <Button variant="contained" onClick={guardar} disabled={ocupado || !nota.trim()}>
                                Guardar
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Button size="small" onClick={() => setEscribiendo(true)} sx={{ alignSelf: 'flex-start' }}>
                        Agregar una observación
                    </Button>
                ))}
        </Stack>
    );
};

export default ContratoApp;
